import { Button, IconButton, Typography } from '@mui/material'
import { Box, SxProps } from '@mui/system'
import DataTable from 'components/DataTable/DataTable'
import { IHeader } from 'components/DataTable/IHeader'
import Loading from 'components/Loading/Loading'
import MonetaryFormat from 'components/MonetaryFormat/MonetaryFormat'
import { GiftCardDetailContract, GiftCardHistoryType } from 'models'
import React, {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useFormat } from 'Services/localize/format'
import nameof from 'utilities/nameof'
import usePaymentContext from '../hook/usePaymentContext'
import TopupIcon from '@mui/icons-material/AddCircleRounded'
import TopupGiftCardPopup from 'components/Sell/TopupGiftCardPopup/TopupGiftCardPopup'
import Decimal from 'decimal.js'
import Guid from 'utilities/Guid'
import dayjs from 'dayjs'
import { useAppSelector } from 'store/useStoreHook'
import usePinDialog from 'components/PinPanel/usePinDialog'
import { SALES_TOP_UP_GIFT_CARD } from 'constant/permissions'
import usePermission from 'hooks/usePermission/usePermission'
import { ENextModule } from 'store/reducers/otherModuleAction.reducer'
import { printCustomerName } from 'components/CustomerAdmin/utils'
import PayButton from './PayButton'
import { sortDate } from 'utilities/date/dateSort'
const style: { [name: string]: SxProps } = {
    button: {
        height: 50,
        textTransform: 'uppercase',
        minWidth: 150,
    } as SxProps,
}
interface UsageHistoryRow {
    historyId: string
    date: string
    isDeduct: boolean
    transaction: string
    amount: number
    by: string
}
const ROWS = nameof<UsageHistoryRow>()

interface IProps {
    giftcard: GiftCardDetailContract | undefined
    onBack: () => void
    loading: boolean
    onUpdateGiftcard: (gc: GiftCardDetailContract) => void
}
function GiftCardDetail({
    giftcard,
    onUpdateGiftcard,
    onBack,
    loading,
}: IProps) {
    const { customerList, orderCustomerID } = usePaymentContext()
    const [giftCardBalance, setGiftCardBalance] = useState(
        giftcard?.currentBalance ?? 0,
    )
    const { hasPermissionWithOther, clearPermission } = usePermission()
    const { togglePinDialogDispatch } = usePinDialog()
    const user = useAppSelector((state) => state.auth.user)
    const { shortDateFormat, currency, dateTimeFormat } = useFormat()

    const unableUseGiftCard = useMemo(
        () =>
            giftcard?.isDisabled || Boolean(giftcard?.expiryDate)
                ? dayjs(giftcard!.expiryDate!!).isBefore(dayjs())
                : false,
        [giftcard],
    )
    const giftcardOwner = useMemo(
        () =>
            customerList.find(
                (a) => a.customerID === giftcard?.customerOwnerID,
            ),
        [customerList, giftcard?.customerOwnerID],
    )
    const [openTopup, setOpenTopup] = useState(false)
    const onCloseTopup = useCallback(() => {
        setOpenTopup(false)
        clearPermission()
    }, [clearPermission])
    const [giftcardHistories, setgiftcardHistories] = useState<
        UsageHistoryRow[]
    >([])
    const onSuccessTopup = useCallback(
        (_giftCardSellID: string, topUpAmount: number) => {
            setOpenTopup(false)
            var newValue: UsageHistoryRow = {
                historyId: Guid.newGuid().toString(),
                date: dayjs().toISOString(),
                isDeduct: false,
                transaction: 'Topup',
                amount: topUpAmount,
                by: user?.userName!,
            }
            const histories = [newValue, ...giftcardHistories]
            setgiftcardHistories(histories)
            const currentBalance = giftCardBalance + topUpAmount
            setGiftCardBalance(currentBalance)
            onUpdateGiftcard({ ...giftcard!, currentBalance: currentBalance })
        },
        [
            giftCardBalance,
            giftcard,
            giftcardHistories,
            onUpdateGiftcard,
            user?.userName,
        ],
    )

    const hasTopupPermission = hasPermissionWithOther([SALES_TOP_UP_GIFT_CARD])
    const onClickTopup = useCallback(() => {
        setOpenTopup(true)
        if (!hasTopupPermission) {
            togglePinDialogDispatch(true, ENextModule.ToptupGiftCard)
        }
    }, [hasTopupPermission, togglePinDialogDispatch])

    const headers: IHeader[] = useMemo(
        () => [
            {
                label: 'Date',
                sortable: true,
                value: (rowData: UsageHistoryRow) =>
                    shortDateFormat(rowData.date),
                comparator: (a: UsageHistoryRow, b: UsageHistoryRow) =>
                    sortDate(dayjs(a.date), dayjs(b.date)),
            },
            {
                label: 'Transaction',
                value: ROWS.transaction,
            },
            {
                label: 'Amount',
                align: 'end',
                itemStyle: { textAlign: 'end' },
                value: (rowData: UsageHistoryRow) => {
                    return (
                        <MonetaryFormat
                            forceMonetaryFormat={
                                rowData.isDeduct ? 'minus' : 'plus'
                            }
                            value={rowData.amount}
                        ></MonetaryFormat>
                    )
                },
            },
            {
                label: 'By',
                value: ROWS.by,
            },
        ],
        [shortDateFormat],
    )

    useEffect(() => {
        setGiftCardBalance(giftcard?.currentBalance ?? 0)
    }, [giftcard?.currentBalance])

    useEffect(
        () =>
            setgiftcardHistories(
                giftcard?.giftCardHistories?.map((gh) => {
                    return {
                        historyId: gh.giftCardHistoryID!,
                        date: gh.createdOn!,
                        transaction: gh.comment ?? gh.type!,
                        amount: gh.amount!,
                        by: gh.createUserName ?? '',
                        isDeduct:
                            gh.type === GiftCardHistoryType.Deduct ||
                            gh.type === GiftCardHistoryType.Transferred,
                    }
                }) ?? [],
            ),
        [giftcard?.giftCardHistories],
    )
    if (loading)
        return (
            <Box className="box">
                <Loading />
            </Box>
        )
    return (
        <React.Fragment>
            <DataDisplayLeftRight
                left={
                    <Typography color={'primary'}>Gift Card Code:</Typography>
                }
                right={giftcard?.issueNo ?? ''}
            />
            <DataDisplayLeftRight
                left={<Typography color={'primary'}>Customer: </Typography>}
                right={giftcardOwner ? printCustomerName(giftcardOwner) : '-'}
            />
            <DataDisplayLeftRight
                left={<Typography color={'primary'}>Expiration: </Typography>}
                right={
                    giftcard?.expiryDate ? (
                        <>
                            {dateTimeFormat(giftcard.expiryDate)}
                            {unableUseGiftCard ? (
                                <Typography ml={1} color={'error'}>
                                    (Expired)
                                </Typography>
                            ) : null}
                        </>
                    ) : (
                        '-'
                    )
                }
            />

            <Box textAlign={'center'}>
                <DataDisplayLeftRight
                    left={
                        <Typography variant="h5" fontWeight={600}>
                            Gift Card Balance:
                        </Typography>
                    }
                    right={
                        <Typography
                            variant="h5"
                            color={'primary'}
                            fontWeight={600}
                        >
                            {currency(giftCardBalance, giftcard?.currencyCode!)}
                            <IconButton
                                disabled={
                                    unableUseGiftCard ||
                                    !(giftcard?.canTopup ?? false)
                                }
                                color="primary"
                                onClick={onClickTopup}
                            >
                                <TopupIcon style={{ fontSize: 28 }}></TopupIcon>
                            </IconButton>
                        </Typography>
                    }
                />
            </Box>
            <Box textAlign={'center'}>
                <DataDisplayLeftRight
                    left={
                        <Typography variant="subtitle2">
                            Initail Value:
                        </Typography>
                    }
                    right={
                        <Typography variant="subtitle2">
                            {currency(
                                giftcard?.initialValue ?? 0,
                                giftcard?.currencyCode!,
                            )}
                        </Typography>
                    }
                />
            </Box>
            <Box
                sx={{
                    pt: 1,
                    overflowY: 'auto',
                    height: `calc(75svh - 270px)`,
                }}
            >
                <DataTable
                    initOrder={[
                        {
                            column: 'Date',
                            direction: 'desc',
                            isUniqueColumn: false,
                        },
                    ]}
                    selectMode={false}
                    headers={headers}
                    data={giftcardHistories}
                    itemKey={ROWS.historyId}
                    usePagination={false}
                    initialRowPerPage={5}
                />
            </Box>
            <Box textAlign={'right'} pt={2}>
                <Box pr={2} component="span">
                    <Button
                        onClick={onBack}
                        variant="outlined"
                        color="primary"
                        sx={style.button}
                    >
                        BACK
                    </Button>
                </Box>
                <PayButton
                    onClickTopup={onClickTopup}
                    giftcardDetail={giftcard!}
                    isExpired={unableUseGiftCard}
                ></PayButton>
            </Box>
            <TopupGiftCardPopup
                customerID={orderCustomerID}
                open={openTopup}
                onDismiss={onCloseTopup}
                onSuccessfulTopup={onSuccessTopup}
                customers={customerList}
                topUpGiftCard={{
                    giftCardID: giftcard?.giftCardID ?? '',
                    issueNo: giftcard?.issueNo ?? '',
                    currentBalance: new Decimal(giftCardBalance),
                    customerEmail: giftcardOwner?.email ?? '',
                }}
            />
        </React.Fragment>
    )
}
const DataDisplayLeftRight = ({
    left,
    right,
}: {
    left: string | ReactNode
    right: string | ReactNode
}): JSX.Element => {
    return (
        <Box pb={0.5}>
            <Box display={'inline-flex'} pr={1}>
                {left}
            </Box>
            <Box display={'inline-flex'}>{right}</Box>
        </Box>
    )
}
export default GiftCardDetail
